import {useCallback, useEffect, useRef, useState} from "react";
import {sendEvent} from "../utils/ga";
import {sessionConfig} from "../config/session";

import {
  QueueStatus,
  setOnAdmitRequired,
  clearAdmitToken,
  clearTicket,
  enterQueue,
  fetchQueueStatus,
  getAdmitToken,
  getAdmitTokenExp,
  getTicket,
  isTicketRequired,
  rememberLastToken,
  sendHeartbeat,
  setWaitingRoomOwnsHeartbeat,
} from "../utils/waitingRoom";

/**
 * 入場制御（待合室）のガード。 issue #2992 / 報告書 §8 No.11
 *
 * ■ 流れ
 *   1. プランページに来たら `enter` を叩く（対象外なら即時トークンが返り、待機画面は出ない）
 *   2. 順番が来るまで `status` を `pollSec` ごとに確認する
 *   3. 順番が来たら 10 秒カウントダウン（＋ボタン）で手続きへ
 *   4. 手続き中は `heartbeatMin` ごとに滞在を通知する（同時滞在数の推定に使う）
 *
 * ■ ★失敗しても順番は失わない
 *   整理番号は端末（localStorage）にあり、サーバーも再発番しない。
 *   状態確認が失敗したら間隔を 10 → 20 → 30 秒と延ばす（混雑時に待合室自体を殴らないため）。
 *
 * ■ ★トークン期限切れは `priority` で再入場
 *   手続き制限時間（30 分）とトークンの期限は同じ。切れたら列の先頭側に戻す。
 *   docs/29 のデメリット ⑥「30 分制限との相互作用（再入場は優先）」への対応。
 */

/**
 * 状態確認に失敗したときのバックオフ（秒）。docs/34 §8 と同じ 10 → 20 → 30。
 * ★`失敗回数 - 1` を添字にする。1 回目の失敗でいきなり 20 秒待たせない。
 */
const ERROR_BACKOFF_SEC = [10, 20, 30];
const backoffSecFor = (failures: number): number =>
  ERROR_BACKOFF_SEC[Math.min(Math.max(failures - 1, 0), ERROR_BACKOFF_SEC.length - 1)];
/** 順番到来から自動遷移までの秒数（バックグラウンドタブ対策でボタンも出す） */
const ADMIT_COUNTDOWN_SEC = 10;

/**
 * 無操作の判定（2026-09-06 先方レビュー）。
 *
 * ★待機画面の文言「予約ページへ移動後、30分間操作がない場合はご案内を終了します」に合わせる。
 *   これまで無操作の打ち切りは**入力フォームだけ**（`checkSession`）で、プランページには
 *   絶対上限しか無かった。放置されたプランページが `maxActive` の枠を持ち続けてしまう。
 */
const IDLE_LIMIT_MS = sessionConfig.idleMinutes * 60000;
/** 無操作かどうかを見る間隔 */
const IDLE_CHECK_MS = 60000;
/** 「操作」とみなすイベント */
const ACTIVITY_EVENTS = ["click", "keydown", "scroll", "touchstart"] as const;

export type WaitingRoomState = {
  /** 待機画面を出すべきか */
  isWaiting: boolean;
  /** 判定が終わるまで true（この間は本体を描画しない） */
  isChecking: boolean;
  status?: QueueStatus;
  isError: boolean;
  nextCheckSec: number;
  admitCountdownSec: number;
  updatedAt?: Date;
  /** 「今すぐ再確認する」 */
  retry: () => void;
  /** 「予約ページへ進む」 */
  enterSite: () => void;
};

export const useWaitingRoom = (planId?: string): WaitingRoomState => {
  const [status, setStatus] = useState<QueueStatus | undefined>(undefined);
  const [isChecking, setIsChecking] = useState(true);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [nextCheckSec, setNextCheckSec] = useState(0);
  /**
   * 状態確認の試行回数。成功・失敗のたびに +1 する。
   *
   * ★これを予約 effect の依存に入れないと、**2 回続けて失敗したときにポーリングが止まる**
   *   （2026-09-03 レビュー §2-D）。2 回目の catch は `isError` / `isWaiting` /
   *   `isChecking` を同じ値に設定するだけなので再レンダーが起きず、
   *   次の `setTimeout` が張られない＝画面が固まったまま順番を待ち続ける。
   */
  const [attempt, setAttempt] = useState(0);
  const [admitCountdownSec, setAdmitCountdownSec] = useState(ADMIT_COUNTDOWN_SEC);
  const [updatedAt, setUpdatedAt] = useState<Date | undefined>(undefined);

  const errorCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  /**
   * `enter` が失敗したときに裏で再試行するタイマー（2026-09-03 再レビュー R2）。
   * ★`clearTimers`（＝待機ポーリング用）とは分ける。`release()` で消してしまうと、
   *   素通しした直後に再試行が止まる。
   */
  const enterRetryRef = useRef<ReturnType<typeof setTimeout>>();
  const tickRef = useRef<ReturnType<typeof setInterval>>();
  const enteredRef = useRef(false);
  /**
   * `enter` が進行中か。 2026-09-07 再レビュー
   * ★初回・バックオフ再試行・`check` の再発番・再入場の**全経路で共有**する。
   *   別々に投げると、初回が未解決のうちに 401 が来たときなどに
   *   **同時に複数の番号を取ってしまう**（§2-I）。
   */
  const enteringRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = undefined;
    }
  }, []);

  /** 待機を終える（本体を描画する） */
  const release = useCallback(() => {
    clearTimers();
    setIsWaiting(false);
    setIsChecking(false);
  }, [clearTimers]);

  /** 裏での `enter` 再試行を止める（応答が取れたら不要・R2） */
  const clearEnterRetry = useCallback(() => {
    if (enterRetryRef.current) {
      clearTimeout(enterRetryRef.current);
      enterRetryRef.current = undefined;
    }
  }, []);

  /** アンマウントで裏の再試行も止める */
  useEffect(() => clearEnterRetry, [clearEnterRetry]);

  /** 応答を反映する。順番が来ていれば待機を終える */
  const apply = useCallback((next: QueueStatus) => {
    clearEnterRetry();
    setStatus(next);
    setUpdatedAt(new Date());
    setIsError(false);
    errorCountRef.current = 0;

    if (!next.enabled || next.admitted) {
      if (next.admitted && next.enabled) {
        sendEvent("waiting_room_admitted", "waiting_room");
      }
      // 到来したら 10 秒後に自動遷移（ボタンでも進める）
      if (next.enabled && next.admitted && isWaiting) {
        setAdmitCountdownSec(ADMIT_COUNTDOWN_SEC);
        return;
      }
      release();
      return;
    }
    setIsWaiting(true);
    setIsChecking(false);
  }, [isWaiting, release, clearEnterRetry]);

  /** 1 回だけ順番を確認する */
  const check = useCallback(async () => {
    const ticket = getTicket();
    if (!ticket) {
      // まだ整理券が無い（初回 enter が失敗した）。enter からやり直す（§2-I）
      // ★他の経路が enter 中なら投げない（2026-09-07 再レビュー）
      if (enteringRef.current) {
        setAttempt((prev) => prev + 1);
        return;
      }
      enteringRef.current = true;
      try {
        apply(await enterQueue(planId, false));
      } catch {
        errorCountRef.current += 1;
        setIsError(true);
      } finally {
        enteringRef.current = false;
        setAttempt((prev) => prev + 1);
      }
      return;
    }
    try {
      apply(await fetchQueueStatus(ticket));
    } catch (e) {
      // ★400 `TICKET_REQUIRED` は同じ整理券では直らない（2026-09-03 再レビュー R5）。
      //   世代を進めた／整理券が 24 時間で切れた場合で、区別せずにバックオフ再試行すると
      //   **永久に待機画面のまま**になる。整理券を捨てて `enter` からやり直す。
      if (isTicketRequired(e)) {
        clearTicket();
        try {
          apply(await enterQueue(planId, false));
        } catch {
          errorCountRef.current += 1;
          setIsError(true);
          setIsWaiting(true);
          setIsChecking(false);
        }
      } else {
        // ★順番は失われない（整理券は端末にある）。間隔を延ばして再確認する
        errorCountRef.current += 1;
        setIsError(true);
        setIsWaiting(true);
        setIsChecking(false);
      }
    } finally {
      // ★成否にかかわらず必ず進める。これが無いと 2 回目以降の失敗で
      //   state が変わらず、次の確認が予約されない（§2-D）
      setAttempt((prev) => prev + 1);
    }
  }, [apply, planId]);

  /** 次の確認を予約する（成功時はサーバー指示、失敗時はバックオフ） */
  useEffect(() => {
    if (!isWaiting || status?.admitted) {
      return;
    }
    const sec = isError ? backoffSecFor(errorCountRef.current) : status?.pollSec ?? 10;
    setNextCheckSec(sec);
    tickRef.current = setInterval(() => setNextCheckSec((p) => (p > 0 ? p - 1 : 0)), 1000);
    timerRef.current = setTimeout(() => {
      check();
    }, sec * 1000);
    return clearTimers;
    // ★`attempt` を依存に入れる。同じ失敗が続いても毎回この effect を張り直すため（§2-D）
  }, [isWaiting, isError, status, attempt, check, clearTimers]);

  /** 順番到来の 10 秒カウントダウン */
  useEffect(() => {
    if (!isWaiting || !status?.admitted) {
      return;
    }
    const id = setInterval(() => {
      setAdmitCountdownSec((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          release();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isWaiting, status?.admitted, release]);

  /**
   * `enter` が失敗したときの裏での再試行（2026-09-03 再レビュー R2）。
   *
   * ★画面は止めない。待合室は OFF が既定で、OFF でも `enter` は必ず呼ばれるため、
   *   ここで待機画面に落とすと `queue` 関数のコールドスタート超過・障害・通信断で
   *   **通常日の全利用者がプランページを開けなくなる**（「OFF なら挙動が変わらない」に反する）。
   *   ON のときは最初の API が 401 を返し、共通ハンドラが `enter` し直して待機画面へ導く。
   */
  const scheduleEnterRetry = useCallback(() => {
    if (enterRetryRef.current) {
      clearTimeout(enterRetryRef.current);
    }
    const run = () => {
      enterRetryRef.current = undefined;
      // ★他の経路が enter 中なら投げ直さず、次のバックオフで様子を見る（2026-09-07 再レビュー）
      if (enteringRef.current) {
        enterRetryRef.current = setTimeout(run, backoffSecFor(errorCountRef.current) * 1000);
        return;
      }
      enteringRef.current = true;
      enterQueue(planId, false)
        .then((next) => {
          if (next.enabled && !next.admitted) {
            sendEvent("waiting_room_enter", "waiting_room");
          }
          apply(next);
        })
        .catch(() => {
          errorCountRef.current += 1;
          enterRetryRef.current = setTimeout(run, backoffSecFor(errorCountRef.current) * 1000);
        })
        .finally(() => {
          enteringRef.current = false;
        });
    };
    enterRetryRef.current = setTimeout(run, backoffSecFor(errorCountRef.current) * 1000);
  }, [apply, planId]);

  /** 初回：列に並ぶ（対象外なら即座に通る） */
  useEffect(() => {
    if (enteredRef.current) {
      return;
    }
    enteredRef.current = true;
    (async () => {
      // ★他の経路（401 など）が先に enter していたら投げない（2026-09-07 再レビュー）
      if (enteringRef.current) {
        return;
      }
      enteringRef.current = true;
      try {
        // ★「トークンを持っていれば待機をスキップ」はしない（2026-09-03 レビュー §2-E/§2-F）。
        //   対象外プランで得た bypass トークンを持って対象プランを開けば素通りできてしまう。
        //   既存の整理券・トークンは enter に渡すので、サーバーの判定に従う（呼び出しは安い）。
        const next = await enterQueue(planId, false);
        if (next.enabled && !next.admitted) {
          sendEvent("waiting_room_enter", "waiting_room");
        }
        apply(next);
      } catch {
        // ★素通しする（2026-09-03 再レビュー R2）。
        //   待合室は OFF が既定なので、`queue` が落ちているだけで全員を止めてはいけない。
        //   ON なら最初の API が 401 を返し、共通ハンドラが priority つきで enter し直す
        //   （priority は期限切れトークンの提示が必須なので、この経路で割り込みは起きない）。
        errorCountRef.current += 1;
        release();
        scheduleEnterRetry();
      } finally {
        enteringRef.current = false;
      }
    })();
  }, [planId, apply, release, scheduleEnterRetry]);

  /**
   * 最後に操作した時刻。 2026-09-06
   * ★入場が確定した時点から数える（待合室で待った時間は「操作」ではない）。
   */
  const lastActivityRef = useRef(Date.now());
  const isIdle = useCallback(() => Date.now() - lastActivityRef.current >= IDLE_LIMIT_MS, []);

  /**
   * 操作の記録。
   * ★間引かない（2026-09-06 再レビュー）。1 分に 1 回に絞ると、**間引かれた最後の操作**が
   *   捨てられて、実際より早く「無操作」と判定されることがある。ref への代入だけなので
   *   毎イベント記録してもコストは無い（再レンダーも起きない）。
   */
  useEffect(() => {
    if (isWaiting || isChecking) {
      return;
    }
    lastActivityRef.current = Date.now();
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, {passive: true}));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [isWaiting, isChecking]);

  /**
   * 列に並び直す（再入場）。 2026-09-06 再レビュー
   *
   * ★入口は 3 つある（**無操作の打ち切り** / **401 ADMIT_REQUIRED** / **トークン期限の監視**）。
   *   別々に `enter` を投げると、401 が並列で来たときや無操作の処理中に期限が来たときに
   *   **同時に複数の番号を取ってしまう**。1 つの関数にまとめ、`enteringRef` で排他する（初回・再試行・`check` とも共有）。
   *
   * @param reason  ログ・GA 用
   * @param priority トークン期限切れからの復帰なら true（列の先頭側へ）
   */
  const reenter = useCallback((reason: "idle" | "admit-required" | "expired", priority: boolean) => {
    if (enteringRef.current) {
      return;
    }
    enteringRef.current = true;
    // ★予約済みのバックオフ再試行は解除する（並び直しと二重に投げない）
    clearEnterRetry();
    if (reason === "idle") {
      // ★無操作の打ち切りは「同じ番号で戻さない」。証明になるトークンごと捨てる
      //   （`priority: false` なので `expiredToken` は送られない）
      clearAdmitToken();
      clearTicket();
      sendEvent("waiting_room_idle_timeout", "waiting_room");
    } else {
      // ★消す前に控える（2026-09-06）。`"admit-required"` は `handleAdmitRequired` が
      //   控え済みだが、期限タイマー（`"expired"`）はここが唯一の経路。
      //   控えないと `expiredToken` が付かず、`cap` の内側で切れた人が**列の後ろに回される**
      rememberLastToken();
      clearAdmitToken();
      sendEvent("waiting_room_reentry", "waiting_room");
    }
    (async () => {
      try {
        apply(await enterQueue(planId, priority));
      } catch {
        // ★古い応答を残さない（2026-09-07 再レビュー）。
        //   `admitted: true` のままだと、順番到来のカウントダウンが動いて 10 秒後に
        //   本体へ戻り、トークンが無いので 401 →再入場…を繰り返す
        setStatus(undefined);
        setIsError(true);
        setIsWaiting(true);
        setIsChecking(false);
        setAttempt((prev) => prev + 1);
      } finally {
        // ★次の判定は「今」から数え直す。並び直した直後に列が空で即入場すると、
        //   最終操作時刻が古いままなので**毎分「無操作」と判定し続ける**
        lastActivityRef.current = Date.now();
        enteringRef.current = false;
      }
    })();
  }, [apply, planId, clearEnterRetry]);

  /**
   * ★無操作が `idleMinutes` を超えたら**ご案内を終了する**（2026-09-06）。
   *   トークンと整理券を捨てて `enter` からやり直す＝新しい番号で待機画面に戻る。
   *   放置されたタブが `maxActive` の枠を持ち続けるのを防ぐ。
   *   待合室が OFF（＝トークンが無い）なら何もしない。
   */
  useEffect(() => {
    if (isWaiting || isChecking) {
      return;
    }
    const id = setInterval(() => {
      if (!getAdmitToken() || !isIdle()) {
        return;
      }
      reenter("idle", false);
    }, IDLE_CHECK_MS);
    return () => clearInterval(id);
  }, [isWaiting, isChecking, isIdle, reenter]);

  /**
   * ★滞在通知はこのフックが受け持つ（2026-09-06 再レビュー）。
   *   `FormSessionProvider` も heartbeat を送っており、プランページでは 2 本になる。
   *   Provider 側には「無操作なら送らない」が無いため、放置タブが枠を持ち続けてしまう。
   */
  useEffect(() => {
    setWaitingRoomOwnsHeartbeat(true);
    return () => setWaitingRoomOwnsHeartbeat(false);
  }, []);

  /** 手続き中の滞在通知 */
  useEffect(() => {
    if (isWaiting || isChecking) {
      return;
    }
    const min = status?.heartbeatMin ?? 5;
    // ★無操作の間は送らない（2026-09-06）。送り続けると、放置されたタブが
    //   `maxActive` の枠を持ち続けて実滞在より多く見える
    const send = () => {
      if (isIdle()) {
        return;
      }
      sendHeartbeat();
    };
    send();
    const id = setInterval(send, min * 60000);
    return () => clearInterval(id);
  }, [isWaiting, isChecking, status?.heartbeatMin, isIdle]);

  /**
   * トークンの期限が来たのに再発行されていない＝**絶対上限（`cap`）に達した**。 2026-09-04
   *
   * ★`heartbeat` は `cap` を超えて再発行しない。気づかずに操作を続けると、
   *   次の API で 401 を受けるまで「切れていること」が分からない。
   *   期限に合わせてタイマーを 1 本張り、切れていたら共通ハンドラ（＝並び直し）へ渡す。
   *   `cap` を過ぎた証明は priority として認められないので、**新しい番号で待機画面**に戻る。
   */
  const [tokenWatch, setTokenWatch] = useState(0);
  useEffect(() => {
    if (isWaiting || isChecking) {
      return;
    }
    const exp = getAdmitTokenExp();
    if (!exp) {
      return;
    }
    const id = setTimeout(() => {
      if (getAdmitToken()) {
        // heartbeat が再発行していた。次の期限で張り直す
        setTokenWatch((prev) => prev + 1);
        return;
      }
      // ★期限切れからの復帰は priority（証明が通れば同じ番号で戻れる）
      reenter("expired", true);
    }, Math.max(0, exp - Date.now()) + 1000);
    return () => clearTimeout(id);
  }, [isWaiting, isChecking, tokenWatch, reenter]);

  /**
   * 401 ADMIT_REQUIRED（トークン期限切れ）→ priority で再入場。
   * ★同時に複数の API が 401 を返すことがある（画面は並列に叩く）。
   *   進行中の再入場があれば無視する（`reenter` が排他する・§2-I）。
   */
  useEffect(() => {
    setOnAdmitRequired(() => reenter("admit-required", true));
    return () => setOnAdmitRequired(undefined);
  }, [reenter]);

  const retry = useCallback(() => {
    clearTimers();
    check();
  }, [clearTimers, check]);

  return {
    isWaiting,
    isChecking,
    status,
    isError,
    nextCheckSec,
    admitCountdownSec,
    updatedAt,
    retry,
    enterSite: release,
  };
};
